import { renderNodeTitle } from "src/app/features/ConfigurationTransfer/components/trees/TreeNodeTitle";
import type { EntityTreeNode, TransferableEntity } from "src/app/features/ConfigurationTransfer/types/ConfigurationTransfer";

// NOTE: This order is intentional as dependencies are processed sequentially and dependencies order must be honored
const TABLE_EXPORT_ORDER: string[] = [
    "CARD_IMPLEMENTATION_LIST",
    "CARD_IMPLEMENTATION",
    "CARD_IMPLEMENTATION_ATTRIBUTE",
    "EMV_KERNEL_CONFIGURATION",
    "AID_PREFERENCE_XML",
    "EMV_KERNEL_CFG_TO_AID_PREF_XML",
    "CAP_KEY_XML",
    "EMV_KERNEL_CFG_TO_CAP_KEY_XML",
    "EMV_CONFIG_XML",
    "EMV_KERNEL_CFG_TO_EMV_CFG_XML",
    "FEP_RESPONSE_CODES",
    "HOST_CONFIG_XML",
    "MERCHANT_REFERENCE_NUMBER",
    "ADDITIONAL_AMOUNT_HANDLING",
    "ACQUIRER",
    "ACQUIRER_TO_VIRTUAL",
    "POS_TYPE",
    "TERMINAL_CONFIGURATION",
    "HARDWARE_PROFILE",
    "DYNAMIC_PROPERTY_GROUP",
    "DYNAMIC_PROPERTY_TO_GROUP",
    "TERMINAL_PROFILE",
    "VAT_CODE",
    "VAT_VALUES",
    "UNIT_MAPPING",
    "PRODUCT",
    "PRODUCT_PROFILE",
    "PRODUCT_TO_PRODUCT_PROFILE",
    "LOYALTY_MATRIX",
    "LOYALTY_MESSAGES",
    "HOST_PRODUCT_CODES",
    "DIALOG_PROFILE",
    "PRINTING_PROFILE",
    "DCC_PROFILE",
    "ALLOWED_PRODUCT_PROFILE",
    "APPLICATION_PROFILE_LIST",
    "CARD_TYPE",
    "APPLICATION_PROFILE",
    "CONTRACT_PROFILE",
    "ACCEPTED_CARDS_CONT_PROFILE",
    "DYNAMIC_PROPERTY_VALUE",
];

export const orderTableNamesForExport = (tableNames: string[]): string[] => {
    const availableTableNames = new Set(tableNames);

    return [
        ...TABLE_EXPORT_ORDER.filter((name) => availableTableNames.has(name)),
        ...tableNames.filter((name) => !TABLE_EXPORT_ORDER.includes(name)),
    ];
};

export const getUniqueValues = (values: string[]): string[] => Array.from(new Set(values));

/**
 * Transforms flat entity list into hierarchical tree structure
 */
export const transformToTreeData = (entities: TransferableEntity[]): EntityTreeNode[] => {
    const entityMap = new Map<string, TransferableEntity>();
    entities.forEach((entity) => entityMap.set(entity.tableName, entity));

    const treeData: EntityTreeNode[] = [];

    // Build top-level nodes (entities that aren't dependencies of others shown)
    entities.forEach((entity) => {
        if (!entity.allowed) {
            return; // Skip disallowed entities
        }

        const children: EntityTreeNode[] = [];

        // Normalize duplicated backend dependencies to keep tree keys stable.
        const uniqueDependencies = getUniqueValues(entity.dependencies);

        // Add dependencies as children (showing what's required)
        uniqueDependencies.forEach((depName) => {
            const depEntity = entityMap.get(depName);
            if (depEntity?.allowed) {
                children.push({
                    key: `${entity.tableName}-dep-${depName}`,
                    title: renderNodeTitle(depEntity, true, entities),
                    disabled: !depEntity.allowed,
                    isDependency: true,
                    entity: depEntity,
                });
            }
        });

        const node: EntityTreeNode = {
            key: entity.tableName,
            title: renderNodeTitle(entity, false, entities),
            disabled: !entity.allowed,
            children: children.length > 0 ? children : undefined,
            entity,
        };

        treeData.push(node);
    });

    return treeData;
};

/**
 * Filters tree nodes based on search value
 */
export const filterTreeNodes = (nodes: EntityTreeNode[], searchValue: string): EntityTreeNode[] => {
    if (!searchValue) {
        return nodes;
    }

    const lowerSearch = searchValue.toLowerCase();

    const filterNode = (node: EntityTreeNode): EntityTreeNode | null => {
        const entity = node.entity;
        const titleMatch = entity?.displayName.toLowerCase().includes(lowerSearch) || entity?.tableName.toLowerCase().includes(lowerSearch);

        const filteredChildren = node.children?.map(filterNode).filter((n): n is EntityTreeNode => n !== null) || [];

        if (titleMatch || filteredChildren.length > 0) {
            return {
                ...node,
                children: filteredChildren.length > 0 ? filteredChildren : undefined,
            };
        }

        return null;
    };

    return nodes.map(filterNode).filter((n): n is EntityTreeNode => n !== null);
};

/**
 * Gets all keys from tree nodes recursively
 */
export const getAllKeys = (nodes: EntityTreeNode[]): React.Key[] => {
    const keys: React.Key[] = [];
    const traverse = (node: EntityTreeNode) => {
        // Only include non-dependency nodes as selectable keys
        if (!node.isDependency) {
            keys.push(node.key);
        }
        node.children?.forEach(traverse);
    };
    nodes.forEach(traverse);
    return keys;
};
